<script setup lang="ts">
import { DEFAULT_LLM } from '@/utils/constants'
import { ref, onMounted, watch, computed, nextTick, defineAsyncComponent } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useTheme as useVuetifyTheme } from 'vuetify'
import Forecast from './components/Forecast.vue'
import CloseButton from './components/CloseButton.vue'
import AppHeader from './components/AppHeader.vue'
import ChatBar from './components/ChatBar.vue'
const AuthScreen = defineAsyncComponent(() => import('./components/AuthScreen.vue'))

const Location = defineAsyncComponent(() => import('./components/Location.vue'))
const Settings = defineAsyncComponent(() => import('./components/Settings.vue'))
const Install = defineAsyncComponent(() => import('./components/Install.vue'))
const About = defineAsyncComponent(() => import('./components/About.vue'))
const GDPR = defineAsyncComponent(() => import('./components/GDPR.vue'))
const Terms = defineAsyncComponent(() => import('./components/Terms.vue'))
const ReleaseNotes = defineAsyncComponent(() => import('./components/ReleaseNotes.vue'))
const Faq = defineAsyncComponent(() => import('./components/Faq.vue'))
const NotificationsScreen = defineAsyncComponent(() => import('./components/NotificationsScreen.vue'))
// Async: the canvas, gesture engine and image cache are dead weight for
// everyone who never turns Pro on.
const ChartsScreen = defineAsyncComponent(() => import('./components/charts/ChartsScreen.vue'))
import { useSettingsStore } from './stores/useSettingsStore'
import { useNavigationStore } from './stores/useNavigationStore'
import { useAuthStore } from './stores/useAuthStore'
import { useUrlParams } from './composables/useUrlParams'
import { useDeviceHandling } from './composables/useDeviceHandling'
import { useTouchHandling } from './composables/useTouchHandling'
import { useTheme } from './composables/useTheme'
import { autoDetectLocation } from './composables/useAutoDetectLocation'
import analytics from './utils/analytics'
import { iosNotificationService } from '@/utils/iosNotificationService'

const { t: $t } = useI18n()
const vuetifyTheme = useVuetifyTheme()
const { effectiveTheme, initializeTheme } = useTheme()

const router = useRouter()
const settings = useSettingsStore()
const navStore = useNavigationStore()
const auth = useAuthStore()

const { loadFromUrlParams } = useUrlParams()
const { setupDeviceHandling } = useDeviceHandling()
const { handleTouchStart, handleTouchMove, handleTouchEnd } = useTouchHandling()

const MAX_FORECAST_DAY = 6
const activeTab = ref('') // Start with no active tab

// Where Terms / Privacy return to when closed. They are reachable from two
// places that are not each other's neighbours: About, and the subscription
// paywall inside Charts. Closing always landed on About, which from the
// paywall meant App Review taps Privacy, closes it, and the paywall is gone
// — so remember the screen that opened it (#199).
const legalReturnTab = ref('AboutScreen')

function openLegal(screen: 'GDPRScreen' | 'TermsScreen', from: string) {
  legalReturnTab.value = from
  activeTab.value = screen
}
const locationSelectorRef = ref()
const settingsRef = ref()


const applyForecastDay = (day: number) => {
  if (typeof day !== 'number' || Number.isNaN(day)) return
  navStore.setDay(Math.max(0, Math.min(Math.round(day), MAX_FORECAST_DAY)))
}

// Add handler for bottom navigation
const handleTabChange = (newTab: string) => {
  // Only change tab if it's different from current tab
  if (newTab !== activeTab.value) {
    activeTab.value = newTab
  }
}

// Function to validate location before navigation
const validateLocationAndNavigate = () => {
  if (!settings.location || !settings.location.trim()) {
    // Trigger validation error message in LocationSelector component
    document.dispatchEvent(new CustomEvent('triggerLocationValidation'))
    // Stay on LocationScreen, don't navigate
    return false
  }
  // Location is valid, allow navigation
  activeTab.value = 'ForecastScreen'
  return true
}

// Function to validate settings before navigation (specifically for Settings screen)
const validateSettingsAndNavigate = () => {
  // Check if settings have unsaved changes and revert them
  if (settingsRef.value && settingsRef.value.hasChanges()) {
    if (import.meta.env.DEV) console.log('🔧 App: Reverting unsaved settings changes')
    settingsRef.value.revertSettings()
  }
  
  // Always close Settings and navigate to Forecast
  activeTab.value = 'ForecastScreen'
  return true
}

const handleGoToForecast = () => {
  activeTab.value = 'ForecastScreen'
}

// AppHeader icon taps set activeTab directly. Language is handled locally
// inside AppHeader (globe icon opens the LanguagePicker dialog). Tapping
// the logo/wordmark returns to the forecast AND snaps viewMode back to
// 'main' (#31) — Forecast.vue's navStore.requestedViewMode watcher picks
// that up and switches the detail view without needing a per-view back
// button inside Forecast.vue itself.
const handleHeaderNavigate = (tab: string) => {
  if (tab === 'ForecastScreen' || tab === 'AboutScreen' ||
      tab === 'LocationScreen' || tab === 'SettingsScreen' ||
      tab === 'ChartsScreen') {
    activeTab.value = tab
    if (tab === 'ForecastScreen') {
      navStore.setViewMode('main')
    }
  }
}


onMounted(async () => {
  // Hook for the Expo WebView shell to hydrate auth after a future
  // native sign-in path (passkey / biometric) without reloading the
  // page. Currently unused — kept as a stable bridge surface so a
  // shell update doesn't need a backend round-trip when added.
  ;(window as any).__witalNativeAuth = async (jwt: string, u: { id: string; email: string; name: string }) => {
    try {
      const { useAuthStore } = await import('@/stores/useAuthStore')
      const auth = useAuthStore()
      await auth.setSession(jwt, u)
      // Nudge the Expo shell to register the APNs push token for this
      // new session right away — without this it only registers on the
      // next cold start when INJECT_JS picks the token out of localStorage.
      const rnwv = (window as any).ReactNativeWebView
      if (rnwv && typeof rnwv.postMessage === 'function') {
        rnwv.postMessage(JSON.stringify({ type: 'jwt', token: jwt }))
      }
    } catch (e) {
      console.warn('Native auth hydration failed:', e)
    }
  }

  // Initialize theme system
  initializeTheme()
  
  // Apply initial theme
  vuetifyTheme.global.name.value = effectiveTheme.value
  document.documentElement.setAttribute('data-theme', effectiveTheme.value)

  // Watch for theme changes
  watch(effectiveTheme, async (newTheme) => {
    console.log('🎨 Applying theme:', newTheme)
    vuetifyTheme.global.name.value = newTheme
    document.documentElement.setAttribute('data-theme', newTheme)
  })

  // Load preferences, URL params, and device handling.
  //
  // Deep links from the push tap (#155) arrive as
  // `/?location=…&country_code=…&when=today|tomorrow`. loadFromUrlParams()
  // writes the values to localStorage but the settings Pinia store has
  // already been initialised from the *previous* localStorage snapshot at
  // <script setup> time, so we have to re-apply the new location through
  // setLocation() here for the current session to honour it.
  const urlParamsResult = loadFromUrlParams()
  if (urlParamsResult?.location) {
    settings.setLocation(urlParamsResult.location, urlParamsResult.countryCode)
    // Lock the deep-link city against the boot-time pullFromServer race
    // (#174 follow-up). Without this, fetchProfile's server pull arrives
    // ~200ms later and overwrites the deep-link with the user's saved
    // long-term default.
    settings.markDeepLinkLocation()
  }
  if (urlParamsResult?.forecastDay !== undefined) {
    applyForecastDay(urlParamsResult.forecastDay)
  }
  
  if (import.meta.env.DEV) {
    console.log('🔍 App: Starting initialization...')
    console.log('🔍 App: Initial location value:', settings.location)
    console.log('🔍 App: Initial activeTab value:', activeTab.value)
  }
  
  // If the page was opened from /articles via one of the header icons,
  // the URL will carry ?tab=AboutScreen (or LocationScreen/SettingsScreen)
  // so the user lands directly on that screen. Strip the param once
  // consumed so a refresh doesn't re-trigger it.
  const deepLinkedTab = (() => {
    const allowed = new Set(['AboutScreen', 'LocationScreen', 'SettingsScreen'])
    try {
      const url = new URL(window.location.href)
      const tab = url.searchParams.get('tab')
      if (tab && allowed.has(tab)) {
        url.searchParams.delete('tab')
        window.history.replaceState({}, '', url.toString())
        return tab
      }
    } catch {}
    return null
  })()

  if (deepLinkedTab) {
    activeTab.value = deepLinkedTab
  } else if (settings.location && settings.location.trim()) {
    activeTab.value = 'ForecastScreen'
  } else {
    // #85: try to land on the Forecast via auto-detect (geolocation
    // → /api/geo IP fallback → reverse-geocode → settings.setLocation).
    // If both paths come up empty, fall back to LocationScreen so the
    // user can pick manually — the gate stays available as a last resort.
    activeTab.value = 'ForecastScreen'
    autoDetectLocation(settings.setLocation, settings.location).then(() => {
      if (!settings.location || !settings.location.trim()) {
        activeTab.value = 'LocationScreen'
      }
    })
  }
  
  if (import.meta.env.DEV) console.log('🔍 App: Final activeTab value:', activeTab.value)
  
  // Setup device handling
  await setupDeviceHandling()
  
  try {
    document.documentElement.style.setProperty('--text-font-multiplier', String(settings.forecastFontSize))
  } catch (e) {
    // no-op
  }

  // Listen for iOS notification taps
  window.addEventListener('notificationTapped', (async (event: CustomEvent) => {
    console.log('📱 App received notificationTapped event:', JSON.stringify(event.detail, null, 2))
    const {
      navigateTo,
      type,
      location: notifLocation,
      countryCode: notifCountryCode,
      language: notifLanguage,
    } = event.detail

    if (navigateTo === 'home') {
      const targetDay = type === 'tomorrow' ? 1 : 0

      // Backend always emits a non-empty location in the deep-link payload
      // (api/src/scheduler.py::_build_push_data). An empty notifLocation
      // here means the shell stripped or failed to parse it — a payload
      // regression worth surfacing for the next person rather than silently
      // landing the user on their previous location.
      if (!notifLocation) {
        console.warn('📱 notificationTapped received empty location — payload regression?', event.detail)
      }

      applyForecastDay(targetDay)
      activeTab.value = 'ForecastScreen'

      await nextTick()

      if (notifLanguage && notifLanguage !== settings.language) {
        await settings.setLanguage(notifLanguage)
        activeTab.value = 'ForecastScreen'
        await nextTick()
      }

      if (notifLocation && notifLocation !== settings.location) {
        settings.setLocation(notifLocation, notifCountryCode)
        // Same race protection as the URL-param path above — warm-tap can
        // also collide with a fetchProfile triggered by a session refresh.
        settings.markDeepLinkLocation()
        activeTab.value = 'ForecastScreen'
        await nextTick()
      }

      setTimeout(() => {
        if (activeTab.value !== 'ForecastScreen') activeTab.value = 'ForecastScreen'
        window.scrollTo({ top: 0, behavior: 'smooth' })
        navStore.setDay(navStore.requestedDay ?? 0)
        navStore.triggerReload()
      }, 200)
    }
  }) as EventListener)

  window.addEventListener('deeplinkNavigate', (async (event: CustomEvent) => {
    const { location: deeplinkLocation, forecastDay: deeplinkDay } = event.detail || {}
    console.log('🔗 Received deeplinkNavigate event:', { deeplinkLocation, deeplinkDay })

    if (deeplinkDay !== undefined) applyForecastDay(deeplinkDay)

    if (deeplinkLocation && deeplinkLocation !== settings.location) {
      settings.setLocation(deeplinkLocation)
      settings.markDeepLinkLocation()
    }

    await nextTick()
    activeTab.value = 'ForecastScreen'

    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' })
      navStore.triggerReload()
      navStore.setDay(navStore.requestedDay ?? 0)
    }, 200)
  }) as EventListener)
  
  // Clear badge and setup notification handlers when app opens
  try {
    // Setup handlers for tap actions FIRST
    iosNotificationService.setupNotificationHandlers()
    
    // Then clear badge
    await iosNotificationService.clearBadge()
  } catch (e) {
    // no-op - not on iOS or service not available
  }
  
  // Track app load completion
  analytics.trackEvent({
    action: 'app_loaded',
    category: 'app_lifecycle',
    label: 'WITAL.AI'
  })
})

// Keep CSS text font multiplier in sync with user setting
watch(() => settings.forecastFontSize, (newVal) => {
  try {
    document.documentElement.style.setProperty('--text-font-multiplier', String(newVal))
  } catch (e) {
    // no-op
  }
})
</script>

<template>
  <v-app :theme="effectiveTheme">
    <v-main class="pa-0 ma-0 safe-main">
    <v-container class="pa-0 ma-0">
      <div
        class="content-wrapper"
        @touchstart="handleTouchStart"
        @touchmove="handleTouchMove"
        @touchend="handleTouchEnd"
      >
        <template v-if="activeTab === 'ForecastScreen'">
          <AppHeader
            :activeTab="activeTab"
            @navigate="handleHeaderNavigate"
          />
          <v-row no-gutters class="ma-0 pa-0">
            <v-col cols="12" class="pa-0 ma-0">
              <Forecast />
            </v-col>
          </v-row>
        </template>

          <v-row no-gutters v-if="activeTab === 'LocationScreen'" class="ma-0 pa-0 settings-fullscreen">
            <v-col cols="12" class="ma-0 pa-0 position-relative settings-col">
              <CloseButton needs-safe-area @click="validateLocationAndNavigate" />
              <Location
                ref="locationSelectorRef"
                v-model="settings.location"
                :selectedLanguage="settings.language"
                @update:modelValue="(newLocation) => { settings.location = newLocation }"
                @update:language="(lang) => settings.setLanguage(lang)"
                @go="(newLocation, newCountryCode) => { settings.setLocation(newLocation, newCountryCode); handleGoToForecast(); }"
              />
            </v-col>
          </v-row>

          <v-row no-gutters v-if="activeTab === 'SettingsScreen'" class="ma-0 pa-0 settings-fullscreen">
            <v-col cols="12" class="ma-0 pa-0 position-relative settings-col">
              <CloseButton needs-safe-area @click="validateSettingsAndNavigate" />
              <Settings
                ref="settingsRef"
                @go="handleGoToForecast"
                @showNotifications="activeTab = 'NotificationsScreen'"
              />
            </v-col>
          </v-row>

          <!-- InstallScreen -->
<Install v-if="activeTab === 'InstallScreen'" @close="activeTab = 'ForecastScreen'" />

          <!-- AboutScreen -->
          <div v-if="activeTab === 'AboutScreen'" class="about-scroll">
            <About
              @close="activeTab = 'ForecastScreen'"
              @showGDPR="openLegal('GDPRScreen', 'AboutScreen')"
              @showTerms="openLegal('TermsScreen', 'AboutScreen')"
              @showReleaseNotes="activeTab = 'ReleaseNotesScreen'"
              @showFaq="activeTab = 'FaqScreen'"
            />
          </div>

          <!-- GDPR Screen -->
<GDPR v-if="activeTab === 'GDPRScreen'" @close="activeTab = legalReturnTab" />

          <!-- Terms and Conditions Screen -->
<Terms v-if="activeTab === 'TermsScreen'" @close="activeTab = legalReturnTab" />

          <!-- Release Notes Screen -->
<ReleaseNotes v-if="activeTab === 'ReleaseNotesScreen'" @close="activeTab = 'AboutScreen'" />

          <!-- FAQ Screen (#50) -->
<Faq v-if="activeTab === 'FaqScreen'" @close="activeTab = 'AboutScreen'" />

          <!-- Notifications Screen (from Settings) -->
<NotificationsScreen v-if="activeTab === 'NotificationsScreen'" @close="activeTab = 'SettingsScreen'" />

          <!-- Charts (#185). Owns its own fixed, full-bleed root so
               it escapes the 640px column; a square chart squeezed into
               a phone-width strip on a desktop monitor is unusable. -->
<ChartsScreen
  v-if="activeTab === 'ChartsScreen'"
  @close="activeTab = 'ForecastScreen'"
  @sign-in="auth.requestAuthDialog()"
  @show-terms="openLegal('TermsScreen', 'ChartsScreen')"
  @show-privacy="openLegal('GDPRScreen', 'ChartsScreen')"
/>
        </div>
      </v-container>
    </v-main>

    <!-- Chat bar (#32) — in-place composer. Typing happens directly in
         the pill; reply bubbles print above the pill inside the same
         portrait-column dock (no modal, no slide-up sheet). Only
         mounted on the forecast screen; other screens have their own
         CloseButton flow. -->
    <ChatBar v-if="activeTab === 'ForecastScreen'" />

    <!-- Global sign-in dialog — opened by any component that calls
         auth.requestAuthDialog() (currently the chat rate-limit CTA).
         The Settings screen keeps its own inline dialog so that flow
         is unchanged. Auto-dismiss on successful login. -->
    <v-dialog
      v-model="auth.authDialogRequested"
      max-width="420"
      @update:model-value="(v) => { if (!v) auth.dismissAuthDialog() }"
    >
      <AuthScreen @close="auth.dismissAuthDialog()" />
    </v-dialog>

  </v-app>
</template>

<style>
/* Global styles */
body {
  font-family: 'Roboto', sans-serif;
  margin: 0;
  padding: 0;
  background-color: var(--bg-primary);
  overflow: hidden;
  position: fixed;
  width: 100%;
  height: 100%;
  touch-action: pan-x pan-y;
  -webkit-tap-highlight-color: transparent;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  user-select: none;
  -webkit-overflow-scrolling: touch;
  /* Prevent double-tap zoom on iOS Safari */
  -webkit-text-size-adjust: 100%;
  -ms-text-size-adjust: 100%;
}

#app {
  height: 100vh;
  width: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  /* Additional iOS zoom prevention */
  touch-action: manipulation;
}

/* Prevent zoom on all interactive elements */
*, *:before, *:after {
  -webkit-tap-highlight-color: transparent;
  -webkit-touch-callout: none;
}

/* Specific rules to prevent double-tap zoom */
button, input, select, textarea, a, [role="button"] {
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}

/* Base font multiplier for responsive typography */
:root {
  --base-font-multiplier: 1.0; /* Default for mobile */
  --app-column-max-width: 640px; /* Centred column; gutters on wider viewports */
  /* Single source of truth for the top safe-area inset. PWA users get the
     real env() value; the native shell overrides this to 0 below because
     its SafeAreaView already pads the WebView below the system status bar.
     Without this indirection, Android Chromium WebView double-counts the
     inset (#96) — it reports the activity's WindowInsets inside the WebView
     even when the WebView is already positioned below them. */
  --safe-area-top: env(safe-area-inset-top);
}

/* Native shell (set by mobile/app/index.jsx INJECT_JS before first paint).
   SafeAreaView already handled the top inset natively, so the page must
   not add it again. */
.in-app {
  --safe-area-top: 0px;
}

/* Prevent zoom on form elements */
input[type="text"], input[type="email"], input[type="tel"], 
input[type="number"], input[type="password"], textarea, select {
  font-size: 16px !important; /* Prevents zoom on iOS when input is focused */
}

.v-application {
  background-color: var(--bg-gutter) !important;
  height: 100vh;
  width: 100%;
  overflow: hidden !important;
  margin: 0 !important;
  padding: 0 !important;
}

.v-main {
  flex: 1;
  overflow: hidden !important;
  display: flex;
  flex-direction: column;
  width: 100%;
  margin: 0 !important;
  padding: 0 !important;
  background-color: var(--bg-gutter) !important;
}

/* Safe area utilities for iOS notch/home indicator */
.safe-top {
  padding-top: constant(safe-area-inset-top);
  padding-top: var(--safe-area-top);
}

.safe-bottom {
  padding-bottom: constant(safe-area-inset-bottom);
  padding-bottom: env(safe-area-inset-bottom);
}

.safe-main {
  padding-top: 0; /* top safe-area applied on content wrapper */
}

.v-container {
  height: 100%;
  width: 100% !important;
  max-width: 100% !important;
  margin: 0 !important;
  padding: 0 !important;
  background-color: var(--bg-gutter) !important;
}

.v-card {
  background-color: var(--bg-primary) !important;
  width: 100%;
  margin: 0 !important;
  padding: 0 !important;
}

/* header removed */


/* Mobile styles */
/* All layout rules apply universally: the 640px centred column is the
   single target on phones, landscape phones, tablets, and desktop. */

body, #app, .v-application, .v-main {
  touch-action: manipulation !important;
  -webkit-text-size-adjust: 100% !important;
  -ms-text-size-adjust: 100% !important;
}

.v-btn, .v-tab, .v-card, .v-list-item, [role="button"] {
  touch-action: manipulation !important;
}

.v-container {
  padding: 0 !important;
  width: 100% !important;
  margin: 0 !important;
}

.v-card {
  margin: 0 !important;
  padding: 0 !important;
  width: 100%;
}

.v-card-text {
  padding: 8px !important;
  margin: 0 !important;
}

.v-bottom-navigation {
  height: 84px !important;
  width: auto !important;
  max-width: var(--app-column-max-width);
  left: 0 !important;
  right: 0 !important;
  margin-inline: auto !important;
  padding: 0 8px env(safe-area-inset-bottom) 8px !important;
  background-color: var(--bg-primary) !important;
  position: fixed;
  bottom: 0;
  z-index: 1001;
  pointer-events: auto;
}

.v-bottom-navigation .v-btn {
  height: 56px !important;
  margin-top: 6px !important;
  padding: 0 10px !important;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.content-wrapper {
  padding-top: 0 !important;
  /* Reserve space for the fixed-positioned chat-dock and (when
   * visible) the webcam dock sitting directly above it.
   * --chat-dock-height is published by ChatBar (includes its own
   * safe-area padding) and --webcam-dock-height by WebcamPanel; both
   * collapse to their fallbacks before the observers fire. */
  padding-bottom: calc(var(--chat-dock-height, calc(56px + env(safe-area-inset-bottom))) + var(--webcam-dock-height, 0px)) !important;
  max-width: var(--app-column-max-width);
  margin-inline: auto;
  background-color: var(--bg-primary);
  box-shadow: 0 0 16px rgba(0, 0, 0, 0.35);
}

.ios-native .content-wrapper {
  padding-top: max(var(--safe-area-top), 44px) !important;
}

.ios-native .content-wrapper > .v-row.settings-fullscreen > .v-col {
  padding-top: max(var(--safe-area-top), 44px) !important;
}

.v-bottom-navigation .v-btn .v-icon {
  background-color: rgba(255, 255, 255, 0.10);
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.v-bottom-navigation .v-btn:hover .v-icon {
  background-color: rgba(255, 255, 255, 0.16);
}

.about-scroll {
  height: calc(100vh - (84px + env(safe-area-inset-bottom)));
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 12px;
}

.copyright-footer {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 24px;
  background-color: var(--bg-primary);
  color: var(--text-primary);
  display: flex;
  align-items: center;
  padding-left: 16px;
  font-size: 0.75rem;
  z-index: 1;
}

/* Tighten bottom nav spacing so five buttons fit on small screens without shrinking size */
.bottom-nav-tight .v-btn.nav-btn {
  flex: 1 1 0;
  min-width: 0 !important;
  justify-content: center;
  font-size: calc(0.875rem * var(--base-font-multiplier, 1.0)) !important; /* Adaptive font size */
}

.bottom-nav-tight .v-btn.nav-btn span {
  font-size: calc(0.875rem * var(--base-font-multiplier, 1.0)) !important; /* Adaptive font size for text */
}

.bottom-nav-tight .v-btn.nav-btn .v-icon {
  font-size: calc(1.2rem * var(--base-font-multiplier, 1.0)) !important; /* Adaptive icon size - 1.2rem base */
}

/* Removed Install and About component styles - moved to separate components */

.settings-fullscreen {
  height: 100vh;
  width: auto;
  max-width: var(--app-column-max-width);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  margin-inline: auto !important;
  z-index: 999;
  background-color: var(--bg-primary);
  box-shadow: 0 0 16px rgba(0, 0, 0, 0.35);
}

.settings-col {
  height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
}

/* iOS-specific styles for better orientation handling */
.ios-device {
  /* Prevent iOS from auto-adjusting text size */
  -webkit-text-size-adjust: 100% !important;
  /* Ensure smooth scrolling */
  -webkit-overflow-scrolling: touch !important;
}

</style>

